<template>
  <div class="payment-wall position-relative container-fluid pb-6">
    <!-- Sample figures only, and out of reach: the prompt above is the one thing to act on. -->
    <div class="payment-wall-preview" aria-hidden="true" inert>
      <DashboardPreviewComponent />
    </div>
    <div
      v-if="loadFailed"
      class="payment-wall-retry card position-absolute top-50 start-50 translate-middle text-center shadow"
      data-testid="payment-wall-retry"
    >
      <div class="card-body">
        <p class="mb-3">{{ $t("billing.preview.load_failed") }}</p>
        <button type="button" class="btn btn-primary" @click="retry">
          {{ $t("billing.preview.retry") }}
        </button>
      </div>
    </div>
    <CorporateOnboardingModal
      :key="`payment-wall-${company.id}-${attempt}`"
      :company
      :needs-agreement="needsAgreement"
      :organisation
      :dismissible="canLeave"
      @close="unlock"
      @dismiss="leave"
      @load-failed="loadFailed = true"
      @nothing-to-ask="unlock"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Stands in for every client page while the company still owes its plan (`payment_required` on the company): a mock
 * dashboard behind the onboarding modal. Once the modal has nothing left to ask, or finds nothing to ask in the first
 * place (paid or unlocked since the account was read), the account is reloaded and the real dashboard takes over.
 *
 * An account that also has a company it may use can close the prompt: it is warned that this company stays locked and
 * sent to the consolidated view, which leaves unpaid companies out. An account with nothing else must choose a plan.
 */
import type { DemoOrganisation } from "@/billing/billingTypes";
import CorporateOnboardingModal from "@/billing/CorporateOnboardingModal.vue";
import DashboardPreviewComponent from "@/billing/DashboardPreviewComponent.vue";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useQueryManager } from "@/composables/useQueryManager";
import type { Company } from "@/types";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    company: Company;
    /** The account has another company it may use, so the prompt can be closed. */
    canLeave?: boolean;
  }>(),
  { canLeave: false }
);

const loadFailed = ref(false);
// Remounting the modal makes it read the billing status again.
const attempt = ref(0);

const accountStore = useAccountStore();
const { globalErrorHandler, globalWarningHandler } = useGlobalHandlers();
const { updateQuery } = useQueryManager();
const { t } = useI18n();

const organisation = computed<DemoOrganisation>(() =>
  props.company.dashboard_view === "trader" ? "trader" : "corporate"
);

const needsAgreement = computed(
  () =>
    !props.company.membership_agreement_signed_at &&
    !props.company.has_registered
);

function retry() {
  loadFailed.value = false;
  attempt.value += 1;
}

async function leave() {
  globalWarningHandler(
    t("billing.preview.left_title", { company: props.company.name }),
    t("billing.preview.left_body")
  );
  try {
    await updateQuery({ consolidated: "true", company_id: undefined });
  } catch (error) {
    globalErrorHandler(error);
  }
}

async function unlock() {
  try {
    await accountStore.reload();
  } catch (error) {
    globalErrorHandler(error);
  }
}
</script>

<style scoped lang="scss">
.payment-wall-retry {
  z-index: 1;
  max-width: 24rem;
}

.payment-wall-preview {
  filter: grayscale(0.6) blur(1.5px);
  opacity: 0.55;
  pointer-events: none;
  user-select: none;
}
</style>
